/**
 * Firebase Storage URL 處理工具函數
 */

import { firebaseConfig } from "@/services/firebase/config";

/**
 * 同步建構 Firebase Storage 公開 CDN URL，無需呼叫 SDK getDownloadURL。
 *
 * 適用於 storage.rules 設定為 `allow read: if true` 的 bucket 路徑
 * (drama_covers、companies、actors、products、user_avatars、announcement_images
 * 等)。直接組合 URL 避免每張圖片觸發一次網路請求,瀏覽器會自動 disk-cache。
 *
 * @param rawUrl - 原始 URL 或 storage 路徑;若為空則回傳空字串
 * @param defaultPrefix - 路徑沒有資料夾時自動補上的前綴 (e.g. "company_logos")
 * @returns 完整的 https URL,可直接放進 <img src=...>
 */
export function buildPublicStorageUrl(
  rawUrl: string | undefined,
  defaultPrefix: string
): string {
  return processStorageUrl(rawUrl, defaultPrefix, firebaseConfig.storageBucket);
}

/**
 * 處理 Firebase Storage 路徑，轉換為可用的 URL
 * @param rawUrl - 原始 URL 或路徑
 * @param defaultPrefix - 預設的儲存路徑前綴（例如 "drama_covers"）
 * @param storageBucket - Firebase Storage bucket 名稱（可選，用於構建 URL）
 * @returns 處理後的 URL
 */
export function processStorageUrl(
  rawUrl: string | undefined,
  defaultPrefix: string,
  storageBucket?: string
): string {
  if (!rawUrl) return "";

  // 如果已經是完整的 URL，直接返回
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
    return rawUrl;
  }

  // 處理 gs:// 格式
  let storagePath = rawUrl;
  if (rawUrl.startsWith("gs://")) {
    const match = rawUrl.match(/gs:\/\/[^/]+\/(.+)/);
    if (match) {
      storagePath = match[1];
    }
  }

  // 移除開頭的斜線
  storagePath = storagePath.replace(/^\/+/, "");

  // 如果路徑不包含預設前綴，添加它
  let finalPath = storagePath;
  if (!storagePath.startsWith(`${defaultPrefix}/`)) {
    if (!storagePath.includes("/")) {
      finalPath = `${defaultPrefix}/${storagePath}`;
    } else {
      finalPath = storagePath;
    }
  }

  // 如果有 bucket 名稱，構建 Firebase Storage URL
  if (storageBucket) {
    try {
      const encodedPath = encodeURIComponent(finalPath);
      return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${encodedPath}?alt=media`;
    } catch (e) {
      console.error("Error encoding storage path:", e);
      return "";
    }
  }

  // 返回處理後的路徑（用於 getDownloadURL）
  return finalPath;
}

/**
 * 驗證圖片文件
 * @param file - 要驗證的文件
 * @param maxSizeMB - 最大文件大小（MB），預設 5MB
 * @returns 驗證結果，包含是否有效和錯誤訊息
 */
export function validateImageFile(
  file: File,
  maxSizeMB: number = 5
): { valid: boolean; error?: string } {
  // 檢查文件類型
  if (!file.type.startsWith("image/")) {
    return {
      valid: false,
      error: "請選擇圖片文件",
    };
  }

  // 檢查文件大小
  const maxSize = maxSizeMB * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `圖片大小不能超過 ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
}














